/**
 * Shared site footer (converted from Webflow).
 *
 * Includes the infinite logo marquee (GSAP), the footer link columns
 * (categories from the CMS, company + support links, socials) and the
 * background image. Webflow-specific traces (Powered by Webflow / buy template)
 * are removed.
 */
import type { FC } from 'react'
import type { Category } from '@/types'

import { useEffect, useRef } from 'react'
import Link from 'next/link'

import { gsap } from '@/lib/gsap'

interface FooterProps {
    categories: Category[]
}

const MARQUEE_REPEAT = [0, 1, 2, 3]

const MarqueeRow: FC = () => (
    <div className='infinite-loop'>
        {MARQUEE_REPEAT.map((index) => (
            <div key={index} className='infinite-loop-text-holder'>
                <div className='infinite-loop-logo-holder'>
                    <img src='/images/Mark-Black.svg' loading='lazy' alt='' className='infinite-loop-logo' />
                </div>
                <div className='infinite-loop-dot-container'>
                    <div className='dot' />
                </div>
                <div className='infinite-loop-text'>
                    <h3 className='infinite-text'>Milano</h3>
                </div>
            </div>
        ))}
    </div>
)

export const Footer: FC<FooterProps> = ({ categories }) => {
    const holderRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const holder = holderRef.current
        if (!holder) return

        const tween = gsap.fromTo(
            holder,
            { xPercent: 0 },
            { xPercent: -49.7, duration: 35, ease: 'none', repeat: -1 }
        )

        return () => {
            tween.kill()
        }
    }, [])

    return (
        <div className='footer'>
            <div>
                <div className='infinite-loop-banner'>
                    <div className='infinite-loop-container'>
                        <div ref={holderRef} className='infinite-loop-holder'>
                            <MarqueeRow />
                            <MarqueeRow />
                        </div>
                    </div>
                </div>
            </div>

            <div className='container footer-container'>
                <div className='footer-flex-container'>
                    <div id='w-node-_5de6db3e-329f-d373-c439-88135dc911d1-0a144c54' className='fade-in-move-on-scroll'>
                        <div className='footer-shops-container'>
                            <Link href='/products' className='footer-mark-holder w-inline-block'>
                                <img src='/images/Mark-White.svg' loading='lazy' alt='Milano' className='footer-mark' />
                            </Link>
                            <div className='footer-shops-holder'>
                                <div className='footer-small-icon-holder'>
                                    <img src='/images/Map-Pin-Golden-Icon.svg' loading='lazy' alt='' className='footer-small-icon' />
                                </div>
                            </div>
                            <div className='content'>
                                <h3 className='white-text extra-light'>Find out about the sales at your local shop</h3>
                            </div>
                            <div className='top-margin-l'>
                                <Link href='/contact' className='button footer-button w-button'>
                                    FIND SHOPS
                                </Link>
                            </div>
                        </div>
                    </div>

                    <div className='fade-in-move-on-scroll'>
                        <h2 className='footer-heading'>Categories</h2>
                        <div className='w-dyn-list'>
                            <div role='list' className='w-dyn-items'>
                                {categories.map((category) => (
                                    <div key={category.slug} role='listitem' className='w-dyn-item'>
                                        <div className='fade-in-move-on-scroll'>
                                            <Link href={`/category/${category.slug}`} className='link-block footer-link w-inline-block'>
                                                <div>{category.name}</div>
                                            </Link>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className='fade-in-move-on-scroll'>
                        <h2 className='footer-heading'>Company</h2>
                        {[
                            { label: 'About', href: '/about' },
                            { label: 'Blog', href: '/blog' },
                            { label: 'Contact', href: '/contact' },
                            { label: 'Guarantee', href: '/guarantee' },
                            { label: 'Shipping', href: '/shipping' }
                        ].map((item) => (
                            <div key={item.href} className='fade-in-move-on-scroll'>
                                <Link href={item.href} className='link-block footer-link w-inline-block'>
                                    <div>{item.label}</div>
                                </Link>
                            </div>
                        ))}
                    </div>

                    <div className='fade-in-move-on-scroll'>
                        <h2 className='footer-heading'>Legal</h2>
                        {[
                            { label: 'Privacy Policy', href: '/privacy' },
                            { label: 'Terms & Conditions', href: '/terms' }
                        ].map((item) => (
                            <div key={item.href} className='fade-in-move-on-scroll'>
                                <Link href={item.href} className='link-block footer-link w-inline-block'>
                                    <div>{item.label}</div>
                                </Link>
                            </div>
                        ))}
                    </div>

                    <div className='fade-in-move-on-scroll'>
                        <div className='footer-social-media-container'>
                            <a href='https://facebook.com' target='_blank' rel='noreferrer' className='footer-social-media-holder w-inline-block'>
                                <img src='/images/facebook.svg' loading='lazy' alt='Facebook' className='footer-social-media-icon' />
                            </a>
                            <a href='https://twitter.com' target='_blank' rel='noreferrer' className='footer-social-media-holder w-inline-block'>
                                <img src='/images/twitter.svg' loading='lazy' alt='Twitter' className='footer-social-media-icon' />
                            </a>
                            <a href='https://youtube.com' target='_blank' rel='noreferrer' className='footer-social-media-holder w-inline-block'>
                                <img src='/images/youtube.svg' loading='lazy' alt='YouTube' className='footer-social-media-icon' />
                            </a>
                            <a href='https://instagram.com' target='_blank' rel='noreferrer' className='footer-social-media-holder w-inline-block'>
                                <img src='/images/instagram.svg' loading='lazy' alt='Instagram' className='footer-social-media-icon' />
                            </a>
                        </div>
                    </div>

                    <div id='w-node-_0f174b1a-d806-e98c-ba1c-cb922f75e3c1-0a144c54' className='externals-wrapper'>
                        <div className='external-link-holder'>
                            <div className='crafted-by'>Crafted with care by</div>
                            <a href='https://azwedo.com' target='_blank' rel='noreferrer' className='external-link w-inline-block'>
                                <div>Azwedo</div>
                            </a>
                            <div className='and'>&amp;</div>
                            <a href='https://wedoflow.com' target='_blank' rel='noreferrer' className='external-link w-inline-block'>
                                <div>Wedoflow</div>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div className='footer-background-container'>
                <div className='overlay' />
                <img src='/images/Footer-Image.jpg' loading='lazy' alt='' className='footer-background-image' />
            </div>
            <div className='footer-bg-color' />
        </div>
    )
}
